import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';
import { API, Badge, tableStyle, thStyle, tdStyle, inputStyle, btnSecondary } from './AcademyShared';

const STATUSES = ['present', 'absent', 'late', 'excused'];
export default function AcademyAttendance() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [cohorts, setCohorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState('');
  const [message, setMessage] = useState('');
  const busy = useRef(false);
  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const response = await fetch(`${API}/companies/${companyId}/academy/attendance?view=cohorts`, { headers: getAuthHeaders() });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Unable to load attendance.');
      setCohorts(data.cohorts || []);
    } catch (e) { setError(e.message || 'Unable to load attendance.'); }
    finally { setLoading(false); }
  }, [companyId]);
  useEffect(() => { load(); }, [load]);

  const mark = async (cohort, student, session, status) => {
    if (!status || busy.current) return;
    busy.current = true; setSaving(`${student.enrollment_id}-${session.id}`); setError(''); setMessage('');
    try {
      const response = await fetch(`${API}/companies/${companyId}/academy/sessions/${session.id}/attendance`, {
        method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ records: [{ enrollment_id: student.enrollment_id, status, notes: student.marks?.[session.id]?.notes || '' }] }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Attendance was not saved.');
      setCohorts(previous => previous.map(c => c.id !== cohort.id ? c : { ...c, students: c.students.map(s => {
        if (s.enrollment_id !== student.enrollment_id) return s;
        const old = s.marks?.[session.id]?.status;
        return { ...s, marks: { ...s.marks, [session.id]: { ...s.marks?.[session.id], status } }, missed: s.missed + (status === 'absent' ? 1 : 0) - (old === 'absent' ? 1 : 0) };
      }) }));
      setMessage(`Saved attendance for ${student.name} on ${session.date}.`);
    } catch (e) { setError(e.message || 'Attendance was not saved.'); }
    finally { busy.current = false; setSaving(''); }
  };

  return <main style={{ padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <h2>Attendance</h2><button style={btnSecondary} disabled={loading || !!saving} onClick={load}>Refresh</button>
    </div>
    <p style={{ color: '#64748b' }}>Active cohorts · Only scheduled session dates are shown. Total missed counts classes marked absent; unmarked, future, cancelled and excused sessions are excluded.</p>
    {error && <p role="alert" style={{ color: '#b91c1c' }}>{error}</p>}
    {message && <p role="status" style={{ color: '#15803d' }}>{message}</p>}
    {initialLoading ? <p>Loading attendance…</p> : <>
      {!error && !cohorts.length && <p>No active cohorts.</p>}
      {cohorts.map(cohort => <section key={cohort.id} aria-label={cohort.name} style={{ background: '#fff', border: '1px solid #dbe2ea', borderRadius: 12, marginBottom: 24, overflow: 'hidden' }}>
        <header style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb' }}><h3 style={{ margin: 0 }}>{cohort.name}</h3><p style={{ color: '#64748b', marginBottom: 0 }}>{cohort.cohort_code} · {cohort.students.length} students · {cohort.sessions.length} sessions</p></header>
        {!cohort.sessions.length ? <p style={{ padding: 20 }}>No sessions scheduled for this cohort yet.</p> : <div style={{ overflowX: 'auto', maxHeight: '65vh' }}>
          <table style={tableStyle} aria-label={`${cohort.name} attendance`}>
            <thead><tr>
              <th scope="col" style={{ ...thStyle, position: 'sticky', left: 0, top: 0, background: '#f8fafc', zIndex: 2 }}>Student</th>
              {cohort.sessions.map(session => <th scope="col" key={session.id} title={session.title} style={{ ...thStyle, position: 'sticky', top: 0, background: '#f8fafc', zIndex: 1 }}>
                <div>{session.date}</div><small>{session.start_time?.slice(0,5) || session.title}</small>
                {session.attendance_status === 'submitted' && <div style={{ fontSize: 11 }}>Submitted</div>}
              </th>)}
              <th scope="col" style={{ ...thStyle, position: 'sticky', right: 0, top: 0, background: '#f8fafc', zIndex: 2 }}>Total Missed Classes</th>
            </tr></thead>
            <tbody>
              {!cohort.students.length && <tr><td colSpan={cohort.sessions.length + 2} style={tdStyle}>No enrolled students.</td></tr>}
              {cohort.students.map(student => <tr key={student.enrollment_id}>
                <th scope="row" style={{ ...tdStyle, position: 'sticky', left: 0, background: '#fff', zIndex: 1, textAlign: 'left' }}><div>{student.name}</div><small style={{ color: '#64748b' }}>{student.code}</small>{student.enrollment_status !== 'active' && <div><Badge value={student.enrollment_status}/></div>}</th>
                {cohort.sessions.map(session => {
                  const record = student.marks?.[session.id];
                  const enrolled = !student.enrollment_date || student.enrollment_date <= session.date;
                  const editable = session.editable && enrolled && student.enrollment_status === 'active';
                  return <td key={session.id} style={tdStyle} title={record?.notes || ''}>
                    {editable ? <select aria-label={`${student.name} on ${session.date} ${session.start_time || session.id}`} style={{ ...inputStyle, minWidth: 118 }} value={record?.status || ''} disabled={!!saving} onChange={e => mark(cohort, student, session, e.target.value)}>
                      <option value="" disabled>Unmarked</option>{STATUSES.map(status => <option key={status} value={status}>{status}</option>)}
                    </select> : record?.status ? <Badge value={record.status}/> : <span style={{ color: '#94a3b8' }}>{enrolled ? 'Unmarked' : 'Not enrolled'}</span>}
                    {saving === `${student.enrollment_id}-${session.id}` && <small>Saving…</small>}
                  </td>;
                })}
                <td style={{ ...tdStyle, position: 'sticky', right: 0, background: '#fff', fontWeight: 700, textAlign: 'center', color: student.missed ? '#dc2626' : '#15803d' }}>{student.missed}</td>
              </tr>)}
            </tbody>
          </table>
        </div>}
      </section>)}
    </>}
  </main>;
}
